import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  IconButton,
  Checkbox,
  Box,
  Tooltip,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import CustomizeIcon from "src/@core/components/icons/CustomizeIcon";

/**
 * BusinessCancelDetailDialog Component
 * Dialog for business to respond to customer cancellation requests
 * Business can approve/reject individual item cancellations
 */
const BusinessCancelDetailDialog = ({
  open,
  onClose,
  dialogData,
  tempSelection,
  onCheckboxChange,
  onSubmit,
}) => {
  const getCustomizationDetails = (item) => {
    if (!item?.customizations?.ingredientModifications) return null;

    const actionMap = {
      substitute: (mod) =>
        `substitute ${mod.ingredientName} with ${mod.substituteWith}`,
      remove: (mod) => `remove ${mod.ingredientName}`,
      add: (mod) => `add extra ${mod.ingredientName}`,
    };

    const modifications = item.customizations.ingredientModifications
      .filter((mod) => mod.action !== "no_modification")
      .map((mod) => actionMap[mod.action]?.(mod))
      .filter(Boolean);

    return modifications.length > 0 ? modifications : null;
  };

  const getItemById = (itemId) => {
    if (!dialogData) return null;

    if (dialogData.isVariant && dialogData.variant?.items) {
      return dialogData.variant.items.find(
        (variantItem) => variantItem._id === itemId
      );
    }

    if (!dialogData.isVariant && dialogData.item?.items) {
      return dialogData.item.items.find((subItem) => subItem._id === itemId);
    }

    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        zIndex: 10002, 
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
        }
      }}
    >
      {/* Dialog Header */}
      <DialogTitle sx={{ 
        borderBottom: "1px solid #E0E0E0", 
        pb: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}>
        <Typography variant="h6" sx={{ textTransform: "lowercase" }}>
          {dialogData?.isVariant 
            ? `${dialogData?.itemName} - ${dialogData?.variantName} variant`
            : dialogData?.itemName
          }
        </Typography>
        <IconButton 
          size="small" 
          onClick={onClose}
          sx={{ ml: 2 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Dialog Content */}
      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textTransform: "lowercase" }}>
          select items to cancel:
        </Typography>
        
        <List sx={{ width: "100%" }}>
          {/* Individual Items */}
          {dialogData?.allItemIds.map((itemId) => {
            const itemKey = dialogData.isVariant 
              ? `${dialogData.menuItemId}-${dialogData.variantName}-${itemId}`
              : `${dialogData.menuItemId}-${itemId}`;
            const item = getItemById(itemId);
            const customizationDetails = getCustomizationDetails(item);
            
            // Check if this item is approved (already cancelled)
            const isApproved = dialogData?.approvedItemIds?.includes(itemId);
            const isChecked = isApproved || tempSelection.has(itemKey);
            const isDisabled = isApproved;
            
            return (
              <ListItem 
                key={itemKey}
                sx={{
                  border: "1px solid #E0E0E0",
                  borderRadius: 1,
                  mb: 1,
                  "&:hover": { bgcolor: isDisabled ? "transparent" : "action.hover" },
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  opacity: isDisabled ? 0.5 : 1
                }}
                onClick={() => {
                  if (isDisabled) return; // Prevent action on approved items
                  
                  const newSelection = new Set(tempSelection);
                  if (newSelection.has(itemKey)) {
                    newSelection.delete(itemKey);
                  } else {
                    newSelection.add(itemKey);
                  }
                  onCheckboxChange(newSelection);
                }}
              >
                <Checkbox
                  checked={isChecked}
                  disabled={isDisabled}
                  onChange={() => {
                    if (isDisabled) return; // Prevent action on approved items
                    
                    const newSelection = new Set(tempSelection);
                    if (newSelection.has(itemKey)) {
                      newSelection.delete(itemKey);
                    } else {
                      newSelection.add(itemKey);
                    }
                    onCheckboxChange(newSelection);
                  }}
                  sx={{ mr: 2 }}
                />
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ textTransform: "lowercase" }}
                      >
                        item {isApproved && "(already cancelled)"}
                      </Typography>
                      {customizationDetails && (
                        <Tooltip
                          title={
                            <Box sx={{ p: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  mb: 1,
                                  textTransform: "lowercase",
                                }}
                              >
                                customizations:
                              </Typography>
                              {customizationDetails.map((detail, idx) => (
                                <Typography
                                  key={idx}
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    textTransform: "lowercase",
                                    mb: 0.5,
                                  }}
                                >
                                  • {detail}
                                </Typography>
                              ))}
                            </Box>
                          }
                          arrow
                          placement="top"
                          PopperProps={{ style: { zIndex: 20003 } }}
                          disablePortal
                          componentsProps={{
                            tooltip: {
                              sx: {
                                zIndex: 20003,
                                backgroundColor: "white",
                                color: "black",
                                border: "1px solid #e0e0e0",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                "& .MuiTooltip-arrow": {
                                  color: "white",
                                  "&::before": {
                                    border: "1px solid #e0e0e0",
                                  },
                                },
                              },
                            },
                          }}
                        >
                          <IconButton size="small" sx={{ p: 0.5 }}>
                            <CustomizeIcon size="16" color="#6200EE" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      </DialogContent>

      {/* Dialog Actions */}
      <DialogActions sx={{ borderTop: "1px solid #E0E0E0", p: 2, gap: 1 }}>
        <Button 
          onClick={onSubmit}
          variant="contained"
          sx={{ textTransform: "lowercase", mt: 1 }}
        >
          add to cancellation
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BusinessCancelDetailDialog;

